import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { type OAuthProvider, useAuth } from '../lib/auth'
import { openPortal, startCheckout } from '../lib/billing'
import { type KeyRequest, loadKeyRequest, requestKey } from '../lib/key-request'
import { usePageMeta } from '../lib/meta'
import { type RateAlert, cleanPort, deleteAlert, listAlerts } from '../lib/rates'
import { hubSignInUrl, signInCopy, signUpErrorMessage } from '../lib/shared-sign-in'
import { sharedSignInEnabled } from '../lib/supabase'
import { prewarmAuthChallenge } from '../lib/turnstile'
import { WATCH_EMPTY_LEAD, WATCH_EMPTY_TAIL } from '../lib/watch-lane-copy'

export function AccountPage() {
  usePageMeta(
    'Your account — FreightRatesMCP',
    'Sign in to manage your FreightRatesMCP plan, rate alerts and API key.',
    '/account',
    ['en']
  )
  const { session, profile, loading, isPro, orgPlan, signIn, signInWithProvider, signOut } =
    useAuth()

  if (loading) {
    return <div className="mx-auto max-w-md px-5 py-24 text-center text-muted">Loading…</div>
  }
  if (!session) return <SignIn signIn={signIn} signInWithProvider={signInWithProvider} />

  return (
    <div className="mx-auto max-w-2xl px-5 pt-14 pb-24">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-light">Your account</h1>
          <p className="mt-1 text-sm text-muted">{session.user.email}</p>
        </div>
        <button
          type="button"
          onClick={signOut}
          className="rounded-lg border border-border-strong px-3 py-1.5 text-sm text-muted transition-colors hover:border-signal hover:text-ink"
        >
          Sign out
        </button>
      </div>

      <PlanCard
        isPro={isPro}
        label={orgPlan?.active ? orgPlan.label : 'Pro'}
        periodEnd={profile?.current_period_end ?? orgPlan?.valid_until ?? null}
        canManage={Boolean(profile?.stripe_customer_id)}
      />
      {isPro && <ApiKeyCard />}
      <AlertsCard isPro={isPro} />
    </div>
  )
}

const OAUTH: { id: OAuthProvider; label: string; icon: React.ReactNode }[] = [
  {
    id: 'google',
    label: 'Continue with Google',
    icon: (
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" aria-hidden>
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
        />
      </svg>
    ),
  },
  {
    id: 'github',
    label: 'Continue with GitHub',
    icon: (
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] fill-ink" aria-hidden>
        <path d="M12 1a11 11 0 0 0-3.48 21.44c.55.1.75-.24.75-.53v-1.85c-3.06.67-3.71-1.47-3.71-1.47-.5-1.28-1.23-1.62-1.23-1.62-1-.69.08-.67.08-.67 1.1.08 1.69 1.14 1.69 1.14.98 1.69 2.58 1.2 3.21.92.1-.71.38-1.2.7-1.47-2.44-.28-5.01-1.22-5.01-5.44 0-1.2.43-2.18 1.13-2.95-.11-.28-.49-1.4.11-2.92 0 0 .93-.3 3.05 1.13a10.6 10.6 0 0 1 5.55 0c2.12-1.43 3.05-1.13 3.05-1.13.6 1.52.22 2.64.11 2.92.7.77 1.13 1.75 1.13 2.95 0 4.23-2.58 5.16-5.03 5.43.4.34.75 1.01.75 2.04v3.02c0 .29.2.64.76.53A11 11 0 0 0 12 1Z" />
      </svg>
    ),
  },
  // Apple hidden until the Apple provider is configured in the navo24 auth project. To re-enable, add:
  // { id: 'apple', label: 'Continue with Apple', icon: (
  //   <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] fill-ink" aria-hidden>
  //     <path d="M16.36 12.6c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.19-1.73-1.36-.14-2.65.8-3.34.8-.68 0-1.75-.78-2.87-.76-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.51-.71 2.84-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.08 2.65-2.14.83-1.23 1.18-2.42 1.2-2.48-.03-.01-2.3-.88-2.32-3.5ZM14.2 6.15c.6-.74 1.01-1.75.9-2.77-.87.04-1.94.58-2.57 1.31-.56.65-1.05 1.7-.92 2.7.98.08 1.98-.5 2.59-1.24Z" />
  //   </svg> ) },
]

function SignIn({
  signIn,
  signInWithProvider,
}: {
  signIn: (email: string) => Promise<{ error: string | null }>
  signInWithProvider: (p: OAuthProvider) => Promise<{ error: string | null }>
}) {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [oauthBusy, setOauthBusy] = useState<OAuthProvider | null>(null)
  const copy = signInCopy(sharedSignInEnabled)
  const hubUrl = sharedSignInEnabled ? hubSignInUrl(`${window.location.origin}/account`) : null

  // Start the challenge as soon as the sign-in form is on screen, not when the button is pressed. It
  // runs while the person types their address, so the token is usually already in hand by the click.
  useEffect(() => {
    prewarmAuthChallenge()
  }, [])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return
    setBusy(true)
    setErr(null)
    const { error } = await signIn(email)
    setBusy(false)
    if (error) setErr(sharedSignInEnabled ? signUpErrorMessage(error) : error)
    else setSent(true)
  }

  async function oauth(p: OAuthProvider) {
    setErr(null)
    setOauthBusy(p)
    // On success the browser redirects to the provider; an error means it's not enabled yet.
    const { error } = await signInWithProvider(p)
    if (error) {
      setOauthBusy(null)
      setErr(error)
    }
  }

  return (
    <div className="mx-auto max-w-md px-5 pt-20 pb-24">
      <h1 className="font-display text-3xl font-light">Sign in</h1>
      <p className="mt-2 text-sm text-muted">{copy.intro}</p>
      {!sent && (
        <>
          <div className="mt-8 grid gap-2.5">
            {OAUTH.map(({ id, label, icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => oauth(id)}
                disabled={oauthBusy != null}
                className="flex items-center justify-center gap-2.5 rounded-lg border border-border-strong bg-surface-0 px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:border-signal disabled:opacity-60"
              >
                {icon}
                {oauthBusy === id ? 'Redirecting…' : label}
              </button>
            ))}
          </div>
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-faint">or with email</span>
            <div className="h-px flex-1 bg-border" />
          </div>
        </>
      )}
      {sent ? (
        <div className="mt-8 rounded-xl border border-signal/40 bg-surface-0 p-6 card-shadow">
          <div className="font-display text-lg font-bold">Check your inbox</div>
          <p className="mt-1 text-sm text-muted">
            We sent a sign-in link to <span className="text-ink">{email}</span>. Open it on this
            device to continue.
          </p>
          {copy.sentNote && <p className="mt-2 text-sm text-muted">{copy.sentNote}</p>}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-3">
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="w-full rounded-lg border border-border-strong bg-surface-0 px-4 py-3 text-ink outline-none focus:border-signal"
          />
          {err && <p className="text-sm text-down">{err}</p>}
          <button
            type="submit"
            disabled={busy}
            className="btn-accent w-full rounded-lg px-4 py-3 font-semibold disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Email me a sign-in link'}
          </button>
        </form>
      )}
      {hubUrl && copy.hubLabel && (
        <p className="mt-5 text-sm">
          <a href={hubUrl} className="text-link hover:underline">
            {copy.hubLabel}
          </a>
        </p>
      )}
      <p className="mt-6 text-xs text-faint">
        Free forever to check rates. A Pro plan adds alerts, full history and API access —{' '}
        <Link to="/pricing" className="text-link hover:underline">
          see pricing
        </Link>
        .
      </p>
    </div>
  )
}

function PlanCard({
  isPro,
  label,
  periodEnd,
  canManage,
}: { isPro: boolean; label: string; periodEnd: string | null; canManage: boolean }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function upgrade() {
    setBusy(true)
    setErr(null)
    const { error } = await startCheckout()
    if (error) {
      setBusy(false)
      setErr(
        error === 'unavailable' || error === 'billing_not_configured'
          ? 'Checkout is opening soon. Hold tight.'
          : 'Could not start checkout.'
      )
    }
  }
  async function manage() {
    setBusy(true)
    const { error } = await openPortal()
    if (error) setBusy(false)
  }

  return (
    <div className="mt-8 rounded-xl border border-border-strong bg-surface-0 p-6 card-shadow">
      <div className="flex items-center justify-between">
        <div>
          <div className="mono text-[11px] uppercase tracking-wider text-faint">Plan</div>
          <div className="mt-1 flex items-center gap-2 font-display text-2xl font-semibold">
            {isPro ? label : 'Free'}
            {isPro && (
              <span className="rounded-full bg-signal-soft px-2 py-0.5 text-[11px] font-semibold text-signal">
                active
              </span>
            )}
          </div>
          {isPro && periodEnd && (
            <p className="mt-1 text-xs text-muted">
              {canManage ? 'Renews' : 'Valid until'}{' '}
              {new Date(periodEnd).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </p>
          )}
        </div>
        {isPro ? (
          canManage && (
            <button
              type="button"
              onClick={manage}
              disabled={busy}
              className="rounded-lg border border-border-strong px-4 py-2 text-sm font-semibold transition-colors hover:border-signal disabled:opacity-50"
            >
              Manage billing
            </button>
          )
        ) : (
          <button
            type="button"
            onClick={upgrade}
            disabled={busy}
            className="btn-accent rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {busy ? 'Opening…' : 'Upgrade to Pro · $49/mo'}
          </button>
        )}
      </div>
      {err && <p className="mt-3 text-sm text-muted">{err}</p>}
      {!isPro && (
        <ul className="mt-4 grid gap-1.5 text-sm text-muted sm:grid-cols-2">
          {[
            'Unlimited rate alerts',
            'Full historical rate charts',
            'Rates API + MCP access',
            'Weekly market movers',
          ].map((f) => (
            <li key={f} className="flex gap-2">
              <span className="text-signal">✓</span>
              {f}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ApiKeyCard() {
  // Keys are issued by our team, with the call cap and expiry set for the plan, so a client cannot hand themselves an
  // uncapped key. The request goes into the shared Sales inbox (lib/key-request.ts); this card shows where it stands.
  const [req, setReq] = useState<KeyRequest | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState('')

  useEffect(() => {
    loadKeyRequest().then((r) => r.ok && setReq(r.request))
  }, [])

  async function send() {
    setBusy(true)
    setErr(null)
    const r = await requestKey(note.trim() || undefined)
    setBusy(false)
    if (r.ok) setReq(r.request)
    else
      setErr(
        r.reason === 'not_entitled'
          ? 'An API key comes with a paid plan.'
          : 'Could not send the request. Try again in a minute, or write to sales@navo24.com.'
      )
  }

  const pending = req?.state === 'sent' || req?.state === 'in_progress'
  const since = req?.since
    ? new Date(req.since).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : null

  return (
    <div className="mt-6 rounded-xl border border-border-strong bg-surface-0 p-6 card-shadow">
      <div className="mono text-[11px] uppercase tracking-wider text-faint">API key</div>
      {req && req.keys > 0 && (
        <p className="mt-2 text-sm">
          Your team has {req.keys} active {req.keys === 1 ? 'key' : 'keys'} for the rates API and
          the hosted MCP server. Calls and the days left are on your{' '}
          <a href="/dashboard" className="text-signal">
            dashboard
          </a>
          .
        </p>
      )}
      <p className="mt-2 text-sm text-muted">
        {pending
          ? `Request sent${since ? ` on ${since}` : ''}. ${
              req?.state === 'in_progress'
                ? 'Our team is on it.'
                : 'Our team will issue the key and write to you.'
            }`
          : 'API keys are issued by our team, with the call limit and the dates that match your plan. Ask for one and we will write back with it.'}
      </p>
      {!pending && (
        <>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Optional: what will you connect it to (Claude Desktop, ChatGPT, your own app)?"
            className="mt-3 w-full rounded-lg border border-border-strong bg-surface-0 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={send}
            disabled={busy}
            className="btn-accent mt-3 inline-block rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {busy ? 'Sending…' : req && req.keys > 0 ? 'Request another key' : 'Request a key'}
          </button>
        </>
      )}
      {err && <p className="mt-3 text-sm text-muted">{err}</p>}
    </div>
  )
}

function AlertsCard({ isPro }: { isPro: boolean }) {
  const [alerts, setAlerts] = useState<RateAlert[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    listAlerts()
      .then(setAlerts)
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [])

  async function remove(id: string) {
    await deleteAlert(id).catch(() => {})
    setAlerts((a) => a.filter((x) => x.id !== id))
  }

  return (
    <div className="mt-6 rounded-xl border border-border-strong bg-surface-0 p-6 card-shadow">
      <div className="flex items-center justify-between">
        <div className="mono text-[11px] uppercase tracking-wider text-faint">Rate alerts</div>
        <span className="text-xs text-faint">{isPro ? 'unlimited' : '1 on Free'}</span>
      </div>
      {!loaded ? (
        <p className="mt-3 text-sm text-muted">Loading…</p>
      ) : alerts.length === 0 ? (
        <p className="mt-3 text-sm text-muted">
          {WATCH_EMPTY_LEAD} <span className="text-ink">Watch this lane</span> {WATCH_EMPTY_TAIL}
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {alerts.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 py-2.5">
              <span className="text-sm">
                <span className="text-ink">
                  {cleanPort(a.pol_name, a.pol_code)} → {cleanPort(a.pod_name, a.pod_code)}
                </span>
                <span className="ml-2 text-xs text-faint">±{a.threshold_pct}%</span>
              </span>
              <button
                type="button"
                onClick={() => remove(a.id)}
                className="text-xs text-muted hover:text-down"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
